import { selectOrg, useAuthStore } from '@client/stores/AuthStore';
import { useOrgSettingsStore } from '@client/stores/OrgSettingStore';
import { SYSTEM_ORG_IDS } from '@shared/constants';
import { useMatch } from '@tanstack/react-router';

/**
 * Returns the orgId from the URL or the auth store. If the orgId is not found, it returns -999.
 * Even if the orgId is returned, it does not guarantee that the user is authorized to access the org.
 *
 * @returns {number} - The orgId
 *
 **/
export function useOrgId(): bigint {
    const org = useAuthStore(selectOrg);
    const orgSettingsStore = useOrgSettingsStore();

    // Select the org ID itself, not `{ params }`: a match's params carry every param in the location,
    // so an object here would re-render each caller whenever a child route's param changes (opening
    // a record drawer), and the router does not structurally share selections by default.
    const publicOrgId = useMatch({
        from: '/public/$orgId',
        shouldThrow: false,
        select: ({ params }) => params.orgId,
    });
    const consoleOrgId = useMatch({
        from: '/console/$orgId',
        shouldThrow: false,
        select: ({ params }) => params.orgId,
    });
    const isAdminConsole = useMatch({ from: '/admin-console', shouldThrow: false, select: () => true });

    if (publicOrgId) {
        return publicOrgId;
    }

    if (org) {
        return org.id;
    }

    if (consoleOrgId) {
        return consoleOrgId;
    }

    if (isAdminConsole) {
        return SYSTEM_ORG_IDS.SYSTEM_CONSOLE;
    }

    if (orgSettingsStore.orgSetting?.orgId) {
        return BigInt(orgSettingsStore.orgSetting.orgId);
    }

    return SYSTEM_ORG_IDS.NOT_APPLICABLE;
}
